import React from 'react';
import { Link } from 'react-router';
import {
  EnvelopeIcon,
  BookOpenIcon,
  RocketLaunchIcon,
  CodeBracketIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/layouts';
import { Seo } from '../seo/Seo';
import {
  DeckHero,
  DeckSection,
  DeckSectionHeader,
  DeckPillarGrid,
  useDeckAnimateOnScroll,
} from '../components/landing/deck';

const Support: React.FC = () => {
  const { t } = useTranslation(['support', 'common']);
  useDeckAnimateOnScroll();

  const resources = [
    {
      icon: BookOpenIcon,
      title: t('support:resources.documentation.title'),
      description: t('support:resources.documentation.description'),
      cta: t('support:resources.documentation.cta'),
      link: 'https://docs.callcov.com',
    },
    {
      icon: RocketLaunchIcon,
      title: t('support:resources.faq.title'),
      description: t('support:resources.faq.description'),
      cta: t('support:resources.faq.cta'),
      link: 'https://docs.callcov.com/getting-started/quickstart',
    },
    {
      icon: CodeBracketIcon,
      title: t('support:resources.apiReference.title'),
      description: t('support:resources.apiReference.description'),
      cta: t('support:resources.apiReference.cta'),
      link: 'https://docs.callcov.com/api/overview',
    },
  ];

  return (
    <PublicLayout transparentHeader>
      <Seo route="/support" />

      <DeckHero title={t('support:hero.title')} subtitle={t('support:hero.subtitle')} />

      <DeckSection>
        <DeckSectionHeader badge={t('support:email.eyebrow')} title={t('support:email.title')} />
        <div className="max-w-3xl space-y-6 animate-on-scroll">
          <p className="text-lg text-white/60 leading-relaxed">{t('support:email.description')}</p>
          <a
            href={`mailto:${t('support:email.address')}`}
            className="inline-flex items-center gap-3 text-2xl text-sky-400 hover:text-sky-300 transition-colors"
          >
            <EnvelopeIcon className="w-6 h-6" />
            {t('support:email.address')}
          </a>
          <p className="text-sm text-white/50">{t('support:email.responseTime')}</p>
        </div>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader
          badge={t('support:resources.eyebrow')}
          title={t('support:resources.title')}
        />
        <div className="grid md:grid-cols-3 gap-x-12 gap-y-10">
          {resources.map((r) => (
            <div key={r.title} className="animate-on-scroll">
              <r.icon className="w-6 h-6 text-sky-400 mb-4" />
              <h3 className="text-lg font-medium text-white mb-3">{r.title}</h3>
              <p className="text-sm text-white/50 leading-relaxed mb-5">{r.description}</p>
              <a
                href={r.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300 font-medium transition-colors group"
              >
                {r.cta}
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          ))}
        </div>
      </DeckSection>

      <DeckSection>
        <DeckSectionHeader badge={t('support:tips.eyebrow')} title={t('support:tips.title')} />
        <div className="max-w-3xl animate-on-scroll">
          <p className="text-lg text-white/60 mb-8 leading-relaxed">{t('support:tips.intro')}</p>
          <ul className="space-y-4 text-base text-white/70">
            {(['item1', 'item2', 'item3', 'item4'] as const).map((k, i) => (
              <li key={k} className="flex items-start gap-4">
                <span className="text-sky-400 font-light text-base mt-0.5">{`0${i + 1}`}</span>
                <span>{t(`support:tips.${k}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      </DeckSection>

      <DeckSection size="compact">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 animate-on-scroll">
          <div>
            <h2 className="text-2xl font-light text-white mb-2">{t('support:sales.title')}</h2>
            <p className="text-white/60">{t('support:sales.description')}</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-base font-medium transition-colors group flex-shrink-0"
          >
            {t('support:sales.cta')}
            <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </DeckSection>
    </PublicLayout>
  );
};

export default Support;
